import {api, RequestParam} from "@/shared/api";

import {RecordingStatusPanel, RecordingStatusPanelConfig} from "@/shared/recording/recordingStatusPanel";
import {SwitchWorkingTimeTypeDialog} from "@/features/workingtime/switchWorkingTimeTypeDialog";
import {WorkingTimeInfoPanel} from "@/shared/workingtime/workingTimeInfoPanel";
import {DescriptionTypeahead} from "@/shared/ui/descriptionTypeahead";
import {statusElementsHolder} from "@/shared/recording/statusElementsHolder";
import {
	isRunningWorkingTimeAttachmentEligible,
	isRunningWorkingTimeTypeChangeAllowed,
	isWorkingTimeDateTimeChangeAllowed
} from "@/shared/securityHelper";
import {FormUtil} from "@/shared/forms/formUtil";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import {DOMUtil} from "@/shared/utils/dom";
import {Analytics} from "@/shared/analytics";
import {SecurityStatus} from "@/shared/securityStatus";
import {getCurrentISOOffset, getISOZonedDateTime, parseDateTimeZone, Temporal} from "@/shared/utils/datefunctions";
import {ErrorResponse, RunningWorkingTime, WorkingTime, WorkingTimeStatus, WorkingTimeType} from "@/shared/timr";
import {BreakTimeRecordingMode, WorkingTimeTypeCategory} from "@/shared/timrTypes";
import {promiseFailNow} from "@/shared/utils/promise";
import {removeCustomFieldValues} from "@/shared/customFieldHelper";

export interface WorkingTimeStatusPanelConfig extends RecordingStatusPanelConfig {
	workingTimeTypes: WorkingTimeType[];
	breakTimeTypes: WorkingTimeType[];
}

export class WorkingTimeStatusPanel extends RecordingStatusPanel<WorkingTimeStatus, RunningWorkingTime, WorkingTime> {

	declare private switchWorkingTimeTypeDialog: SwitchWorkingTimeTypeDialog;
	declare private workingTimeInfoPanel: WorkingTimeInfoPanel;
	declare private descriptionTypeahead: DescriptionTypeahead;

	constructor(public config: WorkingTimeStatusPanelConfig) {
		super(config);
	}

	protected init() {
		// create the info panel before super.init() starts the periodical status updater, which
		// runs updateRecordingInfoPanel() and would otherwise deref an undefined workingTimeInfoPanel
		this.workingTimeInfoPanel = new WorkingTimeInfoPanel($("#workingTimeInfoPanel"));
		this.switchWorkingTimeTypeDialog = new SwitchWorkingTimeTypeDialog({
			container: $("#workingTimeSwitchWorkingTimeTypeDialog"),
			workingTimeTypes: this.config.workingTimeTypes,
			breakTimeTypes: this.config.breakTimeTypes
		});

		super.init();

		$(this.switchWorkingTimeTypeDialog).on(SwitchWorkingTimeTypeDialog.EVENT_WORKING_TIME_TYPE_SWITCHED, (_, data: WorkingTimeStatus) => {
			this.clearChanged();
			this.updateStatus(data);
			this.descriptionTypeahead.setNeedsReset();
			if (this.isPaused(data.runningWorkingTime)) {
				this.showStatusUpdateMessage(messages["recording.paused"]);
			} else {
				this.showStatusUpdateMessage(messages["recording.switched"]);
				Analytics.trackEvent("Working Time - Switch", "Working Time");
			}
		}).on(SwitchWorkingTimeTypeDialog.EVENT_CLOSED, () => {
			this.activateUpdater();
			this.endSwitchRecording();
			this.endPauseRecording();
		});

		$(this).on(RecordingStatusPanel.EVENT_QUICK_SWITCHED, (_, data: WorkingTimeStatus) => {
			this.clearChanged();
			this.updateStatus(data);
			this.showStatusUpdateMessage(messages["recording.switched"]);
			Analytics.trackEvent("Working Time - Quick Switch", "Working Time");
			this.endSwitchRecording();
		});

		$(this).on(RecordingStatusPanel.EVENT_QUICK_STARTED, (_, data: WorkingTimeStatus) => {
			this.clearChanged();
			this.updateStatus(data);
			this.showStatusUpdateMessage(messages["recording.started"]);
			this.endQuickStartRecording();
		});

		const workingTimeTypeSelect = this.config.panel.find(".workingTimeType");
		workingTimeTypeSelect.empty();
		workingTimeTypeSelect.change(e => {
			this.selectWorkingTimeType($(e.target).find("option:selected").data("workingTimeType") as WorkingTimeType);
		});
		DOMUtil.fillWorkingTimeTypeSelect(workingTimeTypeSelect, this.config.workingTimeTypes, false);

		this.descriptionTypeahead = new DescriptionTypeahead({
			element: this.config.panel.find(".descriptionTypeahead"),
			url: "filterWorkingTimeDescriptions.json"
		});
		$(this.descriptionTypeahead).on(DescriptionTypeahead.EVENT_CHANGED, () => {
			this.config.panel.find(".description").change();
			this.queueUpdate();
		});

		this.onWorkingTimeTypeChanged();
	}

	protected pluckRunningRecording(recordingStatus: WorkingTimeStatus | null): RunningWorkingTime | null {
		return recordingStatus ? recordingStatus.runningWorkingTime : null;
	}

	protected pluckLastRecording(recordingStatus: WorkingTimeStatus | null): WorkingTime | null {
		return recordingStatus ? recordingStatus.lastWorkingTime : null;
	}

	protected pluckRecordingCategory(): string {
		return "Working Time";
	}

	protected isDateTimeChangeAllowed(): boolean {
		return isWorkingTimeDateTimeChangeAllowed();
	}

	protected isBreakTime(recording: RunningWorkingTime | null) {
		return recording?.workingTimeType?.category === WorkingTimeTypeCategory.BreakTime;
	}

	protected sendUpdate(): JQuery.jqXHR<WorkingTimeStatus> {
		const startDate = $("#workingTimeStatusStartDate").val() as string;
		const startTime = $("#workingTimeStatusStartTime").val() as string || "0";
		const startTimeZone = $("#workingTimeStatusStartTimeZone").val() as string;
		const start = parseDateTimeZone(startDate, startTime, startTimeZone);
		$("#workingTimeStatusStartDateTime").val(getISOZonedDateTime(start));

		const endDate = $("#workingTimeStatusEndDate").val() as string;
		const endTime = $("#workingTimeStatusEndTime").val() as string;
		const endTimeZone = $("#workingTimeStatusEndTimeZone").val() as string;
		const end = parseDateTimeZone(endDate, endTime, endTimeZone);
		$("#workingTimeStatusEndDateTime").val(getISOZonedDateTime(end));

		$("#workingTimeStatusBreakTime").val(($("#workingTimeStatusBreakTime").val() as string).replace(/\D/g, ""));
		const params = $("#workingTimeStatusForm").serialize() + "&" + $.param({
			localTimeZone: getCurrentISOOffset()
		});
		return api("recording/updateWorkingTimeRecording.json")
			.post(params)
			.done((data) => {
				this.changedFields = $.grep(this.changedFields, (value) => {
					return value !== "startDate" && value !== "startTime" && value !== "startTimeZone";
				});
				$("#workingTimeStatusVersion").val(data.runningWorkingTime.version);
				this.updateStatus(data);
			});
	}

	protected shouldUpdateEndDateAndTime(): boolean {
		return !this.switchWorkingTimeTypeDialog.isVisible();
	}

	protected validateSecurityStatus(): boolean {
		if (!SecurityStatus.isAddWorkingTimeAllowed()) {
			BootstrapModal.info({
				title: "account.expired.workingtime.title",
				message: "account.expired.workingtime",
				escapeText: false
			});
			return false;
		}
		return true;
	}

	protected startRecording(): JQuery.jqXHR<WorkingTimeStatus> {
		Analytics.trackEvent("Working Time - Start", "Working Time");
		this.updateStartTime(this.currentStatus);
		const form = $("#workingTimeStatusForm");
		const now = Temporal.Now.zonedDateTimeISO();
		const params = form.serialize() + "&" + $.param({
			localTimeZone: now.offset,
			timestamp: now.epochMilliseconds
		});
		return api("recording/startWorkingTimeRecording.json")
			.post(params)
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.showStatusUpdateMessage(messages["recording.started"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected startPauseRecording(): JQuery.jqXHR {
		Analytics.trackEvent("Working Time - Pause", "Working Time");
		// Recording Mode = MINUTES
		if (globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_NO_POSITION) {
			const now = Temporal.Now.zonedDateTimeISO();
			return api("recording/pauseWorkingTimeRecording.json")
				.post({
					pausedAt: getISOZonedDateTime(now),
					localTimeZone: now.offset,
					recordId: this.config.panel.find(".recordId").val(),
					timestamp: now.epochMilliseconds
				})
				.done((data) => {
					this.updateStatus(data);
					this.showStatusUpdateMessage(messages["recording.paused"]);
					this.descriptionTypeahead.setNeedsReset();
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.code === "E0200") {
						this.fetchRecordingStatus(true);
					}
				})
				.always(() => this.endPauseRecording());
		} else if (globals.breakTimeRecordingMode === BreakTimeRecordingMode.RECORD_WITH_POSITION) {
			// RecordingMode = BOOKING
			this.updateEndTime(this.currentStatus);
			const form = $("#workingTimeStatusForm");
			const params: RequestParam[] = form.serializeArray();
			return api("recording/validateWorkingTimeRecording.json")
				.get(params)
				.done(() => {
					const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
					params.push({name: "changedNewStart", value: changed});
					params.push({name: "localTimeZone", value: getCurrentISOOffset()});
					if (this.config.breakTimeTypes.length > 1) {
						this.switchWorkingTimeTypeDialog.show(params, "pause");
					} else {
						this.switchWorkingTimeType(this.config.breakTimeTypes[0]?.id, params)
							.done(() => this.showStatusUpdateMessage(messages["recording.paused"]))
							.always(() => this.endPauseRecording());
					}
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
					}
					this.endPauseRecording();
				});
		}
	}

	protected startResumeRecording(): JQuery.jqXHR<WorkingTimeStatus> {
		Analytics.trackEvent("Working Time - Resume", "Working Time");
		if (this.getSelectedWorkingTimeType().category === WorkingTimeTypeCategory.BreakTime) {
			const lastWorkingTime: WorkingTime = this.pluckLastRecording(this.currentStatus);
			const workingTimeTypeId = lastWorkingTime?.workingTimeType?.id;
			const form = $("#workingTimeStatusForm");
			const params: RequestParam[] = form.serializeArray();
			const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
			params.push({name: "changedNewStart", value: changed});
			params.push({name: "localTimeZone", value: getCurrentISOOffset()});
			return this.switchWorkingTimeType(workingTimeTypeId, params)
				.done(() => {
					// copy notes from last working time
					this.config.panel.find(".description").val(lastWorkingTime.description);
				})
				.done(() => this.showStatusUpdateMessage(messages["recording.resumed"]))
				.always(() => this.endResumeRecording());
		} else {
			const now = Temporal.Now.zonedDateTimeISO();
			return api("recording/resumeWorkingTimeRecording.json")
				.post({
					resumedAt: getISOZonedDateTime(now),
					localTimeZone: now.offset,
					recordId: this.config.panel.find(".recordId").val(),
					timestamp: now.epochMilliseconds
				})
				.done((data) => {
					this.updateStatus(data);
					this.descriptionTypeahead.setNeedsReset();
					this.showStatusUpdateMessage(messages["recording.resumed"]);
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.code === "E0200") {
						this.fetchRecordingStatus(true);
					}
				}).always(() => this.endResumeRecording());
		}
	}

	protected stopRecording(): JQuery.jqXHR<WorkingTimeStatus> {
		Analytics.trackEvent("Working Time - Stop", "Working Time");
		this.updateEndTime(this.currentStatus);
		const workingTimeType = this.getSelectedWorkingTimeType();
		if (workingTimeType && workingTimeType.category !== WorkingTimeTypeCategory.AttendanceTime) {
			removeCustomFieldValues(this.config.panel);
		}
		const form = $("#workingTimeStatusForm");
		const now = Temporal.Now.zonedDateTimeISO();
		const params = form.serialize() + "&" + $.param({
			localTimeZone: now.offset,
			timestamp: now.epochMilliseconds
		});
		// The recordId of the recording being stopped. If the working time type requires a request
		// (recordingMode BY_REQUEST), no WorkingTime is persisted and lastWorkingTime keeps pointing at
		// whatever it was before - so a mismatch here means a request was created instead.
		const stoppedRecordId = this.pluckRunningRecording(this.currentStatus)?.recordId;
		return api("recording/stopWorkingTimeRecording.json")
			.post(params)
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.descriptionTypeahead.setNeedsReset();
				const requestCreated = data.lastWorkingTime?.recordId !== stoppedRecordId;
				this.showStatusUpdateMessage(requestCreated ? messages["recording.requestCreated"] : messages["recording.stopped"]);
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
			});
	}

	protected deleteRecording(): JQuery.jqXHR<WorkingTimeStatus> {
		Analytics.trackEvent("Working Time - Delete", "Working Time");
		const form = $("#workingTimeStatusForm");
		return api("recording/deleteWorkingTimeRecording.json")
			.post({
				localTimeZone: getCurrentISOOffset(),
				recordId: this.config.panel.find(".recordId").val()
			})
			.done((data) => {
				FormUtil.removeAllFeedback(form);
				this.clearChanged();
				this.updateStatus(data);
				this.descriptionTypeahead.setNeedsReset();
				this.showStatusUpdateMessage(messages["recording.deleted"]);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.code === "E0200") {
					this.fetchRecordingStatus(true);
				}
			});
	}

	protected startSwitchRecording(): JQuery.jqXHR {
		Analytics.trackEvent("Working Time - Switch", "Working Time");
		this.updateEndTime(this.currentStatus);
		const form = $("#workingTimeStatusForm");
		const params: RequestParam[] = form.serializeArray();
		return api("recording/validateWorkingTimeRecording.json")
			.get(params)
			.done(() => {
				const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
				params.push({name: "changedNewStart", value: changed});
				params.push({name: "localTimeZone", value: getCurrentISOOffset()});
				this.switchWorkingTimeTypeDialog.show(params, this.getSelectedWorkingTimeType().category === WorkingTimeTypeCategory.BreakTime ? "resume" : "switch");
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
				this.endSwitchRecording();
			});
	}

	protected startQuickSwitchRecording(recordingId: number): JQuery.jqXHR<WorkingTimeStatus> {
		this.updateEndTime(this.currentStatus);
		const form = $("#workingTimeStatusForm");
		const now = Temporal.Now.zonedDateTimeISO();
		const params: RequestParam[] = form.serializeArray();
		const changed = !this.notChanged("endDate") || !this.notChanged("endTime") || !this.notChanged("endTimeZone");
		params.push({name: "changedNewStart", value: changed});
		params.push({name: "sourceWorkingTimeId", value: recordingId});
		params.push({name: "localTimeZone", value: now.offset});
		params.push({name: "timestamp", value: now.epochMilliseconds.toString()});
		if (this.pluckRunningRecording(this.currentStatus)) {
			Analytics.trackEvent("Working Time - Quick Switch", "Working Time");
			return api("recording/quickSwitchWorkingTimeRecording.json").post(params).done((data) => {
				FormUtil.removeAllFeedback(form);
				$(this).triggerHandler(RecordingStatusPanel.EVENT_QUICK_SWITCHED, [data]);
				this.descriptionTypeahead.setNeedsReset();
			}).fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					FormUtil.markFieldErrors(form, response.responseJSON.fieldErrors);
				}
				this.endSwitchRecording();
			});
		} else {
			Analytics.trackEvent("Working Time - Quick Start", "Working Time");
			return api("recording/quickSwitchWorkingTimeRecording.json")
				.post(params)
				.done((data) => {
					$(this).triggerHandler(RecordingStatusPanel.EVENT_QUICK_STARTED, [data]);
					this.descriptionTypeahead.setNeedsReset();
				}).fail(() => {
					this.resetLoading(this.config.panel.find(".startButton"));
				});
		}
	}

	protected updateRecordingInfoPanel(recording: RunningWorkingTime | null, running: boolean, forceUpdate = true) {
		const workingTimeType = recording && recording.workingTimeType ? recording.workingTimeType : this.getSelectedWorkingTimeType();
		if (workingTimeType) {
			this.workingTimeInfoPanel.update(workingTimeType);
		}
	}

	protected updateCustomFieldsVisibility(workingTimeType: WorkingTimeType) {
		this.config.panel.find("#workingTimeStatusCustomFields").toggle(workingTimeType && workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime);
	}

	protected getSelectedWorkingTimeType(): WorkingTimeType {
		const workingTimeTypeId = parseInt(this.config.panel.find(".workingTimeType").val() as string, 10);
		const allWorkingTimeTypes = this.config.workingTimeTypes.concat(this.config.breakTimeTypes);
		const workingTimeTypes = $.grep(allWorkingTimeTypes, (workingTimeType) => {
			return workingTimeType.id === workingTimeTypeId;
		});
		return workingTimeTypes.length > 0 ? workingTimeTypes[0] : null;
	}

	/**
	 * The working time types that may be selected while a recording is running.
	 *
	 * A recording that collects proof documents keeps its working time type for as long as it runs: attachments the
	 * user already uploaded would be lost when it is changed. And no running recording may be moved into such a type,
	 * because the whole recorded span would retroactively turn into a working time request when it is stopped - so
	 * those types are not offered at all. Switching (stop + start) stays unrestricted, see SwitchWorkingTimeTypeDialog.
	 */
	private getSelectableWorkingTimeTypesForRunningRecording(runningWorkingTime: RunningWorkingTime): WorkingTimeType[] {
		const runningWorkingTimeType = runningWorkingTime.workingTimeType;
		if (isRunningWorkingTimeAttachmentEligible(runningWorkingTimeType)) {
			return [runningWorkingTimeType];
		}
		const workingTimeTypes = this.isBreakTime(runningWorkingTime) ? this.config.breakTimeTypes : this.config.workingTimeTypes;
		return workingTimeTypes.filter((workingTimeType) => isRunningWorkingTimeTypeChangeAllowed(runningWorkingTimeType, workingTimeType));
	}

	protected updateFields(recordingStatus: WorkingTimeStatus) {
		super.updateFields(recordingStatus);

		const selectedWorkingTimeType = this.getSelectedWorkingTimeType();
		if (recordingStatus.runningWorkingTime) {
			DOMUtil.fillWorkingTimeTypeSelect(
				this.config.panel.find(".workingTimeType"),
				this.getSelectableWorkingTimeTypesForRunningRecording(recordingStatus.runningWorkingTime),
				false
			);
			if (this.notChanged("workingTimeType")) {
				this.config.panel.find(".workingTimeType").val(recordingStatus.runningWorkingTime.workingTimeType.id);
			}
		} else {
			DOMUtil.fillWorkingTimeTypeSelect(this.config.panel.find(".workingTimeType"), this.config.workingTimeTypes, false);
			// the last used type is only worth suggesting while it is still selectable, and there may be none at all
			const lastWorkingTimeTypeId = recordingStatus.lastWorkingTime?.workingTimeType?.id;
			const suggestedWorkingTimeType = this.config.workingTimeTypes.find((type) => type.id === lastWorkingTimeTypeId);
			if (suggestedWorkingTimeType && this.notChanged("workingTimeType")) {
				this.config.panel.find(".workingTimeType").val(suggestedWorkingTimeType.id);
				this.workingTimeInfoPanel.update(suggestedWorkingTimeType);
			} else {
				this.workingTimeInfoPanel.update(selectedWorkingTimeType);
			}
		}
		if (selectedWorkingTimeType !== this.getSelectedWorkingTimeType()) {
			this.onWorkingTimeTypeChanged();
		}
		this.updatePanelBorderColor();
	}

	protected updateFieldsWithRunningRecording(runningRecording: RunningWorkingTime) {
		super.updateFieldsWithRunningRecording(runningRecording, runningRecording.workingTimeType.category === WorkingTimeTypeCategory.AttendanceTime);
	}

	protected updatePanelBorderColor() {
		const panelBody = this.config.panel.find(".panel-body");
		const icon = this.config.panel.find(".recording-status-indicator");

		Object.keys(WorkingTimeTypeCategory).map(k => WorkingTimeTypeCategory[k as keyof typeof WorkingTimeTypeCategory])
			.forEach(c => {
				panelBody.removeClass("border-" + c);
				icon.removeClass("icon-" + c);
			});

		const workingTimeType = this.getSelectedWorkingTimeType();
		if (this.pluckRunningRecording(this.currentStatus) && workingTimeType) {
			panelBody.addClass("border-" + workingTimeType.category);
			icon.addClass("icon-" + workingTimeType.category);
		}
	}

	protected onStatusUpdated(recordingStatus: WorkingTimeStatus) {
		super.onStatusUpdated(recordingStatus);
		statusElementsHolder.updateRunningWorkingTime(this.pluckRunningRecording(recordingStatus));
	}

	private selectWorkingTimeType(workingTimeType: WorkingTimeType) {
		this.onWorkingTimeTypeChanged();
		if ($.inArray("workingTimeType", this.changedFields) === -1) {
			this.changedFields[this.changedFields.length] = "workingTimeType";
		}
		this.workingTimeInfoPanel.update(workingTimeType);
		this.updatePanelBorderColor();
		this.queueUpdate();
	}

	private onWorkingTimeTypeChanged() {
		const workingTimeType = this.getSelectedWorkingTimeType();
		this.updateCustomFieldsVisibility(workingTimeType);
		this.descriptionTypeahead.setFilter(workingTimeType ? {workingTimeTypeId: workingTimeType.id} : {});
	}

	private switchWorkingTimeType(workingTimeTypeId: number, params: RequestParam[]): JQuery.jqXHR<WorkingTimeStatus> {
		if (params && workingTimeTypeId) {
			params.push({name: "newWorkingTimeTypeId", value: workingTimeTypeId});
			params.push({name: "timestamp", value: Date.now()});

			return api("recording/switchWorkingTimeRecording.json")
				.post(params)
				.done((data) => {
					FormUtil.removeAllFeedback($("#workingTimeStatusForm"));
					this.clearChanged();
					this.updateStatus(data);
					this.descriptionTypeahead.setNeedsReset();
				});
		} else if (!workingTimeTypeId) {
			BootstrapModal.info({
				title: "dialog.info.title",
				message: "recording.switchToBreakTime.noBreakTime",
			});
			return promiseFailNow() as JQuery.jqXHR<WorkingTimeStatus>;
		}
		return promiseFailNow() as JQuery.jqXHR<WorkingTimeStatus>;
	}
}
